import { z } from "zod";
import validator from "validator";
import { PHONE_ERROR, isValidPhone, normalizePhone } from "./phone";
import {
  filmAgeOptions, filmAiUseOptions, filmAttendInPersonOptions, filmAttendanceOptions, filmBudgetRangeOptions,
  filmCaptionOptions, filmContentAdvisoryOptions, filmDecisionRoleOptions, filmDistributionStatusOptions,
  filmExhibitionFormatOptions, filmFinalCutOptions, filmFinancingSecuredOptions, filmFinancingSourceOptions,
  filmFormatOptions, filmFormatsOfInterestOptions, filmGenreChoices, filmGuardianConsentOptions, filmHeardAboutOptions,
  filmIndustryAgeOptions, filmLookingForOptions, filmMarketingOptInOptions, filmMeetingCapacityOptions,
  filmMeetingDayOptions, filmMeetingModeOptions, filmMusicClearedOptions, filmNetworkTurnoutOptions,
  filmOrganizationTypeOptions, filmPanelInterestOptions, filmPartnerTypeOptions, filmPitchRoleOptions,
  filmPitchTrackOptions, filmPremiereStatusOptions, filmProjectFormatOptions, filmProjectStageOptions,
  filmPublicOnlineOptions, filmRightsAvailableOptions, filmRightsHolderOptions, filmSeekingOptions,
  filmShareChannelOptions, filmSoundOptions, filmStyleOptions, filmSubmitterRoleOptions, filmSubtitleOptions,
  filmTeamIdentityOptions, filmTypeOptions, filmYesNoOptions,
} from "./film-constants";

// Hand-written shapes (village-schema.ts pattern) for the C-Tribe x Film
// forms. Each key is validated on its own step by the [...slug] renderer, so
// cross-field rules (runtime vs format, guardian for under-18) are left to
// the review team.

const REQUIRED = "Field is required";
const SELECT_ONE = "Please select one option.";

const text = (max: number) => z.string({ required_error: REQUIRED, invalid_type_error: REQUIRED })
  .trim().min(1, REQUIRED).max(max, `Use ${max} characters or fewer.`);
const optionalText = (max: number) => z.string().trim().max(max, `Use ${max} characters or fewer.`).nullish();

const countWords = (value: string) => value.trim().split(/\s+/).filter(Boolean).length;
const words = (max: number) => z.string({ required_error: REQUIRED, invalid_type_error: REQUIRED })
  .trim().min(1, REQUIRED).max(20000)
  .refine((value) => countWords(value) <= max, `Use ${max} words or fewer.`);
const optionalWords = (max: number) => z.string().trim().max(20000)
  .refine((value) => countWords(value) <= max, `Use ${max} words or fewer.`).nullish();

const email = z.string({ required_error: REQUIRED }).trim().min(1, REQUIRED).email("Invalid email address").max(254);
const optionalEmail = email.or(z.literal("")).nullish();

const isHttpUrl = (value: string) => validator.isURL(value, { protocols: ["http", "https"], require_protocol: true });
const url = z.string({ required_error: REQUIRED }).trim().min(1, REQUIRED).max(2048)
  .refine(isHttpUrl, "Enter a full link starting with https://");
const optionalUrl = url.or(z.literal("")).nullish();

const phone = z.preprocess((v) => (typeof v === "string" ? normalizePhone(v) : v),
  z.string({ required_error: REQUIRED }).min(1, REQUIRED).max(40).refine(isValidPhone, PHONE_ERROR)).describe("phone");
const optionalPhone = z.preprocess((v) => (typeof v === "string" ? normalizePhone(v) : v),
  z.string().max(40).refine((value) => !value || isValidPhone(value), PHONE_ERROR).nullish()).describe("phone");

const radio = <T extends readonly [string, ...string[]]>(options: T) =>
  z.enum(options, { required_error: SELECT_ONE, invalid_type_error: SELECT_ONE });
const many = <T extends readonly [string, ...string[]]>(options: T, max: number = options.length) =>
  z.array(z.enum(options)).min(1, "Select at least one option.").max(max, `Select up to ${max}.`);
const optionalMany = <T extends readonly [string, ...string[]]>(options: T) =>
  z.array(z.enum(options)).max(options.length).nullish();

// A lone checkbox is submitted by react-hook-form as `false` or its value,
// not an array. Normalise to an array so stored answers match multi-option
// checkboxes.
const singleCheckbox = <T extends string>(option: T) => z.preprocess(
  (value) => (value === false || value === null || value === undefined || value === "" ? [] : typeof value === "string" ? [value] : value),
  z.array(z.literal(option)).max(1)
);

const consent = z.literal("Yes", { errorMap: () => ({ message: "You must accept to submit." }) });

const digits = (min: number, max: number, message: string) => z.string({ required_error: REQUIRED })
  .trim().regex(/^\d{1,3}$/, message).refine((value) => Number(value) >= min && Number(value) <= max, message);
const runtime = digits(1, 600, "Enter whole minutes between 1 and 600, for example 92.");
// Eligibility: completed on or after January 1, 2025.
const completionMonth = z.string({ required_error: REQUIRED }).trim()
  .regex(/^(0[1-9]|1[0-2])\/20(2[5-7])$/, "Use MM/YYYY, for example 03/2026. Films completed before January 2025 are not eligible.");
const projectedMonth = z.string().trim()
  .regex(/^(0[1-9]|1[0-2])\/20(2[5-9]|3\d)$/, "Use MM/YYYY, for example 11/2027.").or(z.literal("")).nullish();

const exclusive = <T extends string>(schema: z.ZodType<T[] | undefined | null>, option: T, label: string) =>
  schema.refine((values) => !values?.includes(option) || values.length === 1, `Choose ${label} on its own, or remove it.`);

const contact = {
  firstName: text(80),
  lastName: text(80),
  email,
  phoneNumber: phone,
  city: text(120),
  country: text(80),
  pronouns: optionalText(40),
};

const ageAndGuardian = {
  ageConfirmation: radio(filmAgeOptions),
  guardianName: optionalText(160),
  guardianEmail: optionalEmail,
  guardianConsent: singleCheckbox(filmGuardianConsentOptions[0]),
};

const heardAbout = {
  heardAbout: radio(filmHeardAboutOptions),
  heardAboutOther: optionalText(120),
};

const audience = {
  networkTurnout: radio(filmNetworkTurnoutOptions),
  shareChannels: optionalMany(filmShareChannelOptions),
};

const teamIdentity = exclusive(optionalMany(filmTeamIdentityOptions), "Prefer not to say", "Prefer not to say");

const finalConsents = {
  privacyAcknowledgement: consent,
  marketingOptIn: singleCheckbox(filmMarketingOptInOptions[0]),
};

export const filmSubmissionSchema = {
  ...contact,
  submitterRole: radio(filmSubmitterRoleOptions),
  submitterRoleOther: optionalText(80),
  ...ageAndGuardian,

  filmTitle: text(200),
  filmTitleEnglish: optionalText(200),
  filmFormat: radio(filmFormatOptions),
  filmType: radio(filmTypeOptions),
  filmStyle: optionalMany(filmStyleOptions),
  filmGenres: many(filmGenreChoices, 3),
  runtimeMinutes: runtime,
  episodeCount: z.string().trim().regex(/^\d{0,2}$/, "Enter a number from 1 to 50.")
    .refine((value) => value === "" || (Number(value) >= 1 && Number(value) <= 50), "Enter a number from 1 to 50.").nullish(),
  completionMonth,
  isWorkInProgress: radio(filmFinalCutOptions),
  countryOfProduction: text(200),
  productionProvince: optionalText(80),
  isAlbertaFilm: radio(filmYesNoOptions),
  languages: text(200),
  hasEnglishSubtitles: radio(filmSubtitleOptions),

  screenerUrl: url,
  screenerPassword: optionalText(100),
  trailerUrl: optionalUrl,

  premiereStatus: radio(filmPremiereStatusOptions),
  previousScreenings: optionalWords(150),
  isPubliclyOnline: radio(filmPublicOnlineOptions),
  upcomingFestivals: optionalWords(100),
  distributionStatus: radio(filmDistributionStatusOptions),
  distributorName: optionalText(200),

  logline: words(40),
  shortSynopsis: words(75),
  longSynopsis: words(300),
  directorStatement: words(250),
  directorName: text(200),
  directorBio: words(150),
  keyCrew: optionalWords(150),
  teamIdentity,
  filmmakerAttendance: radio(filmAttendanceOptions),
  aiUse: radio(filmAiUseOptions),
  aiUseDetail: optionalWords(100),

  exhibitionFormats: exclusive(many(filmExhibitionFormatOptions), "Not sure yet", "Not sure yet"),
  aspectRatio: optionalText(20),
  soundFormat: radio(filmSoundOptions).nullish(),
  captions: radio(filmCaptionOptions),
  describedVideo: radio(filmYesNoOptions).nullish(),
  contentAdvisories: exclusive(optionalMany(filmContentAdvisoryOptions), "None", "None"),

  // Links only until protected storage exists for unreleased material.
  still1: url,
  still2: optionalUrl,
  directorPhoto: optionalUrl,
  pressKitUrl: optionalUrl,
  websiteOrSocial: optionalUrl,

  rightsHolder: radio(filmRightsHolderOptions).refine(
    (value) => value !== filmRightsHolderOptions[2],
    "We can only accept a film from its rights holder or someone they have authorised."
  ),
  musicCleared: radio(filmMusicClearedOptions),
  musicDetail: optionalWords(100),

  ...audience,
  ...heardAbout,
  additionalInfo: optionalWords(150),
  privacyAcknowledgement: finalConsents.privacyAcknowledgement,
  rightsLicence: consent,
  marketingOptIn: finalConsents.marketingOptIn,
};

export const filmPitchSchema = {
  ...contact,
  ...ageAndGuardian,
  pitchRole: radio(filmPitchRoleOptions),
  companyName: optionalText(200),
  websiteOrSocial: optionalUrl,
  applicantBio: words(150),
  pitchTrack: radio(filmPitchTrackOptions),

  projectTitle: text(200),
  projectFormat: radio(filmProjectFormatOptions),
  projectGenres: many(filmGenreChoices, 3),
  logline: words(40),
  synopsis: words(350),
  whyNow: words(200),
  targetAudience: words(150),
  comparables: optionalText(200),
  languages: text(200),
  countryOfProduction: text(200),
  teamBios: words(250),
  teamIdentity,

  // Finished film track
  runtimeMinutes: runtime,
  completionMonth,
  screenerUrl: url,
  screenerPassword: optionalText(100),
  premiereStatus: radio(filmPremiereStatusOptions),
  rightsAvailable: many(filmRightsAvailableOptions),
  salesStatus: optionalWords(150),

  // Project track
  projectStage: radio(filmProjectStageOptions),
  budgetRange: radio(filmBudgetRangeOptions),
  financingSecured: radio(filmFinancingSecuredOptions),
  financingSources: optionalMany(filmFinancingSourceOptions),
  financePlan: optionalWords(200),
  statusReport: words(200),
  projectedCompletion: projectedMonth,

  seeking: many(filmSeekingOptions),
  meetingGoals: words(100),
  partnerTypes: many(filmPartnerTypeOptions),

  pitchDeckUrl: url,
  sizzleUrl: optionalUrl,
  sizzlePassword: optionalText(100),
  attendInPerson: radio(filmAttendInPersonOptions),
  meetingDays: optionalMany(filmMeetingDayOptions),

  ...audience,
  ...heardAbout,
  additionalInfo: optionalWords(150),
  privacyAcknowledgement: finalConsents.privacyAcknowledgement,
  pitchMaterialsConsent: consent,
  marketingOptIn: finalConsents.marketingOptIn,
};

export const filmIndustrySchema = {
  firstName: contact.firstName,
  lastName: contact.lastName,
  email,
  phoneNumber: optionalPhone,
  jobTitle: text(120),
  organizationName: text(200),
  organizationWebsite: url,
  linkedinUrl: optionalUrl,
  city: contact.city,
  country: contact.country,
  organizationType: radio(filmOrganizationTypeOptions),
  organizationTypeOther: optionalText(80),
  decisionRole: radio(filmDecisionRoleOptions),
  lookingFor: many(filmLookingForOptions),
  formatsOfInterest: many(filmFormatsOfInterestOptions),
  genresOfInterest: optionalMany(filmGenreChoices),
  territories: text(200),
  budgetBands: optionalMany(filmBudgetRangeOptions),
  mandate: words(150),
  meetingCapacity: radio(filmMeetingCapacityOptions),
  meetingDays: many(filmMeetingDayOptions),
  meetingMode: radio(filmMeetingModeOptions),
  panelInterest: radio(filmPanelInterestOptions).nullish(),
  ageConfirmation: radio(filmIndustryAgeOptions),
  heardAbout: radio(filmHeardAboutOptions).nullish(),
  heardAboutOther: optionalText(120),
  privacyAcknowledgement: finalConsents.privacyAcknowledgement,
  industryTerms: consent,
  marketingOptIn: finalConsents.marketingOptIn,
};
